/**
 * <g-kanban> - Kanban Board Container
 *
 * Manages SortableJS across all child g-column elements.
 * Emits events for card movements and clicks.
 *
 * @example
 * <g-kanban @card-moved="handleMove($event.detail)" @card-clicked="handleClick($event.detail)">
 *   <g-column status="todo" label="To Do" color="purple">
 *     <g-card card-id="1">...</g-card>
 *   </g-column>
 * </g-kanban>
 *
 * @fires card-moved - When a card is dropped in a new position
 * @fires card-clicked - When a card is clicked (for edit modal)
 */
export class GKanban extends HTMLElement {
  constructor() {
    super();
    this.sortableInstances = [];
    this._initTimeout = null;
    this._isDragging = false;
  }

  connectedCallback() {
    // Set up container styles
    this.style.display = 'flex';
    this.style.gap = '1rem';
    this.style.padding = '1rem';
    this.style.overflowX = 'auto';
    this.style.minHeight = '500px';

    // Wait for child elements to be parsed and rendered
    setTimeout(() => {
      this.initSortable();
    }, 100);

    // Re-initialize when children change (for Alpine x-for updates)
    // Use debounce to avoid constant re-init
    this.observer = new MutationObserver(() => {
      this.debouncedInit();
    });
    this.observer.observe(this, { childList: true, subtree: true });

    // Delegate card clicks
    this.addEventListener('click', this.handleClick.bind(this));
  }

  disconnectedCallback() {
    this.destroy();
    this.observer?.disconnect();
    if (this._initTimeout) clearTimeout(this._initTimeout);
  }

  debouncedInit() {
    // Don't reinit while dragging
    if (this._isDragging) return;

    if (this._initTimeout) clearTimeout(this._initTimeout);
    this._initTimeout = setTimeout(() => {
      if (!this._isDragging) {
        this.initSortable();
      }
    }, 100);
  }

  destroy() {
    this.sortableInstances.forEach(s => s.destroy());
    this.sortableInstances = [];
  }

  initSortable() {
    // Clean up existing instances
    this.destroy();

    // Check if Sortable is available
    if (typeof Sortable === 'undefined') {
      console.error('[g-kanban] SortableJS not found. Include it before using g-kanban.');
      return;
    }

    const group = this.getAttribute('group') || 'g-kanban-shared';
    const columns = this.querySelectorAll('g-column');

    console.log('[g-kanban] Initializing Sortable on', columns.length, 'columns');

    columns.forEach(column => {
      const cards = column.querySelectorAll('g-card');
      console.log('[g-kanban] Column', column.getAttribute('status'), 'has', cards.length, 'cards');

      const sortable = new Sortable(column, {
        group: group,
        animation: 150,
        ghostClass: 'g-card-ghost',
        chosenClass: 'g-card-chosen',
        dragClass: 'g-card-drag',
        draggable: 'g-card',
        filter: '.g-column-header',
        onStart: (evt) => {
          this._isDragging = true;
          console.log('[g-kanban] Drag started:', evt.item.getAttribute('card-id'));
        },
        onEnd: (evt) => {
          this._isDragging = false;
          console.log('[g-kanban] Drag ended:', evt.item.getAttribute('card-id'));
          this.handleDragEnd(evt);
        },
        onUnchoose: () => {
          this._isDragging = false;
        }
      });

      this.sortableInstances.push(sortable);
    });
  }

  handleDragEnd(evt) {
    const card = evt.item;
    const fromColumn = evt.from.closest('g-column');
    const toColumn = evt.to.closest('g-column');

    this.dispatchEvent(new CustomEvent('card-moved', {
      detail: {
        cardId: card.getAttribute('card-id'),
        fromStatus: fromColumn?.getAttribute('status'),
        toStatus: toColumn?.getAttribute('status'),
        oldIndex: evt.oldIndex,
        newIndex: evt.newIndex
      },
      bubbles: true,
      composed: true
    }));
  }

  handleClick(e) {
    const card = e.target.closest('g-card');
    // Don't fire if clicking a button or interactive element inside the card
    if (card && !e.target.closest('button, a, input, select, textarea')) {
      this.dispatchEvent(new CustomEvent('card-clicked', {
        detail: {
          cardId: card.getAttribute('card-id'),
          card: card
        },
        bubbles: true,
        composed: true
      }));
    }
  }
}

customElements.define('g-kanban', GKanban);
